import React from "react";
import { Table, Title } from "melco-ui"; // your styled Table component
import type { ColumnsType } from "antd/es/table";
import type { ControlcenterMelcoModelsMachineLicensePricingInfo as PricingInfo } from "melco-shared-logic";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface MachineLicensesChangeRow {
    key: string;
    title: string;
    current: string;
    new: string;
    change: string;
}

interface Props {
    subscription_start_date: string;
    response: {
        monthly?: PricingInfo;
        yearly?: PricingInfo;
    };
}

// Styled container
const TableContainer = styled.div`
  width: 100%;
  height: auto;
  background: #fff;
`;


// Styled component for the merged cell text
const RightAlignedText = styled.div`
  color: black;
  margin-top:3px;
  margin-bottom:3px;
  margin-left:50%;
  padding:1px;
  font-weight: 600;
`;
// Safe formatter
const formatCurrency = (value?: number) =>
    value !== undefined ? `$${value.toFixed(2)}` : "-";


const MachineLicensesChangeTable: React.FC<Props> = ({ subscription_start_date, response }) => {
    const { t } = useTranslation()



    const computeCharge = (
        data: PricingInfo,

    ): string => {
        const currentLic = data.current_licenses_count ?? 0;
        const newLic = data.new_licenses_count ?? 0;
        const newLicensesCost = data?.new_licenses_cost ?? 0;

        /** Amount = Change in Amount of licenses * new per license cost [ Available in model.
         *  If amount is negative than $0.00] */
        const newValueToCharge = (newLic - currentLic) * newLicensesCost

        if (newValueToCharge > 0) {
            return formatCurrency(newValueToCharge)
        }
        else {
            return formatCurrency(0)
        }
    }

    // Build row data
    const buildTableData = (
        data: PricingInfo,
        period: "month" | "year"
    ): MachineLicensesChangeRow[] => {
        const currentLic = data.current_licenses_count ?? 0;
        const newLic = data.new_licenses_count ?? 0;
        const currentCost = data.current_total_cost ?? 0;
        const newCost = data.new_total_cost ?? 0;
        const currentLicensesCost = data.current_licenses_cost ?? 0;
        const newLicensesCost = data?.new_licenses_cost ?? 0;

        return [
            {
                key: "licenses",
                title: t("machinelicenses.create.changetable.amount_of_licenses"),
                current: data.current_licenses_count?.toString() ?? "-",
                new: data.new_licenses_count?.toString() ?? "-",
                change: `${newLic - currentLic >= 0 ? "+" : ""}${newLic - currentLic} licenses`,
            },
            {
                key: "cost_per_license",
                title: t("machinelicenses.create.changetable.cost_per_license"),
                current: formatCurrency(data.current_licenses_cost),
                new: formatCurrency(data.new_licenses_cost),
                change: `${newLicensesCost - currentLicensesCost >= 0 ? "+" : ""}${formatCurrency(newLicensesCost - currentLicensesCost)}`,
            },
            {
                key: "cost",
                title: t("machinelicenses.create.changetable.cost"),
                current: `${formatCurrency(data.current_total_cost)} / ${period}`,
                new: `${formatCurrency(data.new_total_cost)} / ${period}`,
                change: `${newCost - currentCost >= 0 ? "+" : ""}${formatCurrency(newCost - currentCost)} / ${period}`,
            },

        ];
    };

    const columns: ColumnsType<MachineLicensesChangeRow> = [
        {
            title: "",
            dataIndex: "title",
            key: "title",
        },
        {
            title: t("machinelicenses.create.changetable.current"),
            dataIndex: "current",
            key: "current",
        },
        {
            title: t("machinelicenses.create.changetable.new"),
            dataIndex: "new",
            key: "new",
        },
        {
            title: t("machinelicenses.create.changetable.change"),
            dataIndex: "change",
            key: "change",
        },

    ];

    const totalMonthlyCharge = computeCharge(response.monthly ?? {});
    const totalYearlyCharge = computeCharge(response.yearly ?? {});

    return (
        <>
            {response.monthly && (
                <>
                    <Title level={5}>{t("machinelicenses.create.changetable.monthly")}</Title>
                    <TableContainer>
                        <Table
                            columns={columns}
                            dataSource={buildTableData(response.monthly, "month")}
                            pagination={false}
                            bordered
                        />
                        <RightAlignedText>
                            {t("machinelicenses.create.changetable.total_amount_charged_at")} {subscription_start_date} &nbsp;&nbsp;&nbsp;{totalMonthlyCharge}
                        </RightAlignedText>
                    </TableContainer>

                </>
            )}

            {response.yearly && (
                <>
                    <Title level={5} style={{ marginTop: 24 }}>{t("machinelicenses.create.changetable.yearly")}</Title>
                    <TableContainer>
                        <Table
                            columns={columns}
                            dataSource={buildTableData(response.yearly, "year")}
                            pagination={false}
                            bordered
                        />
                        <RightAlignedText>
                            {t("machinelicenses.create.changetable.total_amount_charged_at")} {subscription_start_date} &nbsp;&nbsp;&nbsp;{totalYearlyCharge}
                        </RightAlignedText>
                    </TableContainer>
                </>
            )}
        </>
    );
};

export default MachineLicensesChangeTable;